<template>
  <div>
    <v-app>

      <!-- Terminal Loader -->
      <transition name="loader-fade">
        <div v-if="loading" class="terminal-loader">
          <div class="terminal-window">
            <div class="terminal-header">
              <div class="terminal-buttons">
                <span class="btn-red"></span>
                <span class="btn-yellow"></span>
                <span class="btn-green"></span>
              </div>
              <div class="terminal-title">cyber@craft:~$</div>
            </div>
            <div class="terminal-body">
              <div
                v-for="(line, index) in displayedLines"
                :key="index"
                class="terminal-line"
                :class="{ 'success-line': line.type === 'success' }"
              >
                <span class="prompt" v-if="line.showPrompt">root@cybercraft:~$</span>
                <span class="command">{{ line.text }}</span>
              </div>
              <div v-if="showCursor" class="terminal-line">
                <span class="prompt">root@cybercraft:~$</span>
                <span class="cursor">█</span>
              </div>
              <div v-if="showProgress" class="progress-wrapper">
                <div class="progress-bar-terminal">
                  <div class="progress-fill-terminal" :style="{ width: loadingProgress + '%' }">
                    <span class="progress-blocks">{{ progressBlocks }}</span>
                  </div>
                </div>
                <span class="progress-percent">{{ loadingProgress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Main App -->
      <transition name="page-fade">
        <div v-if="!loading" class="app-shell">
          <!-- Fixed Header sits here, 80px tall -->
          <Header />

          <!-- All page content starts BELOW the header -->
          <main class="page-content">
            <router-view></router-view>
          </main>

          <Footer />
        </div>
      </transition>

    </v-app>
  </div>
</template>

<script>
import "./assets/style/fonts.css"
import "./assets/style/style.css"
import Header from "./components/HeaderPage.vue"
import Footer from "./components/FooterPage.vue"

export default {
  name: 'App',
  components: {
    Header,
    Footer,
  },
  data: () => ({
    loading: true,
    loadingProgress: 0,
    displayedLines: [],
    showCursor: true,
    showProgress: false,
    terminalLines: [
      { text: 'Initializing system...',        type: 'normal',  showPrompt: true,  delay: 80  },
      { text: '[OK] Network services online',  type: 'success', showPrompt: false, delay: 120 },
      { text: '[OK] Configuration loaded',     type: 'success', showPrompt: false, delay: 120 },
      { text: 'npm run build --production',    type: 'normal',  showPrompt: true,  delay: 150 },
      { text: '✓ Dependencies resolved',       type: 'success', showPrompt: false, delay: 120 },
      { text: '✓ Assets optimized',            type: 'success', showPrompt: false, delay: 120 },
      { text: '[OK] Application ready',        type: 'success', showPrompt: false, delay: 150 },
      { text: 'Launching interface...',        type: 'normal',  showPrompt: true,  delay: 100 },
    ],
  }),
  computed: {
    progressBlocks() {
      return '█'.repeat(Math.floor(this.loadingProgress / 5));
    }
  },
  mounted() {
    document.body.style.overflow = 'hidden';
    this.startTerminalLoader();
  },
  methods: {
    async startTerminalLoader() {
      for (let i = 0; i < this.terminalLines.length; i++) {
        await this.sleep(this.terminalLines[i].delay);
        this.displayedLines.push(this.terminalLines[i]);
      }
      this.showCursor = false;
      this.showProgress = true;
      await this.animateProgress();
      this.displayedLines.push({ text: '✓ Launch complete', type: 'success', showPrompt: false });
      await this.sleep(400);
      document.body.style.overflow = '';
      this.loading = false;
    },
    animateProgress() {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (this.loadingProgress < 100) {
            this.loadingProgress += 10;
          } else {
            clearInterval(interval);
            resolve();
          }
        }, 30);
      });
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

/* ─────────────────────────────────────────────
   APP SHELL — the key fix
   The header is fixed at 80px height.
   .page-content gets padding-top: 80px so no
   page content ever hides behind the header.
───────────────────────────────────────────── */
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #000000;
}

.page-content {
  padding-top: 80px; /* must match your header height exactly */
  flex: 1;
}

/* Hero section exception — if your hero page
   intentionally wants to go full-bleed behind
   the transparent header, add this class to
   that specific page's root element:
   .hero-fullbleed { margin-top: -80px; }
   That way only the hero slides under the header,
   everything else stays below it correctly.       */

/* ─── Vuetify overrides ───────────────────────── */
.v-application,
.v-application--wrap,
.v-main {
  background: #000000 !important;
}

.v-app-bar,
.v-toolbar__content {
  background: transparent !important;
}

/* ─── Terminal Loader ─────────────────────────── */
.terminal-loader {
  position: fixed;
  inset: 0;
  background: #0a0a0a;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.terminal-window {
  width: 100%;
  max-width: 800px;
  background: #1e1e1e;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  animation: terminalFadeIn 0.5s ease-out;
}

@keyframes terminalFadeIn {
  from { opacity: 0; transform: scale(0.96) translateY(20px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.terminal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #2d2d2d;
  border-bottom: 1px solid #3d3d3d;
}

.terminal-buttons {
  display: flex;
  gap: 8px;
}

.terminal-buttons span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.btn-red    { background: #ff5f56; }
.btn-yellow { background: #ffbd2e; }
.btn-green  { background: #27c93f; }

.terminal-title {
  font-family: 'Fira Code', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  flex: 1;
  text-align: center;
  font-weight: 500;
}

.terminal-body {
  padding: 24px;
  min-height: 350px;
  max-height: 70vh;
  overflow-y: auto;
  background: #1e1e1e;
  font-family: 'Fira Code', monospace;
  font-size: 14px;
  line-height: 1.8;
}

.terminal-body::-webkit-scrollbar        { width: 8px; }
.terminal-body::-webkit-scrollbar-track  { background: #2d2d2d; }
.terminal-body::-webkit-scrollbar-thumb  { background: #5ce1e6; border-radius: 4px; }

.terminal-line {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  animation: lineAppear 0.2s ease-out;
}

@keyframes lineAppear {
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
}

.prompt       { color: #5ce1e6; font-weight: 600; }
.command      { color: #e6e6e6; }
.success-line .command { color: #50fa7b; }

.cursor {
  color: #5ce1e6;
  animation: cursorBlink 0.8s step-end infinite;
}

@keyframes cursorBlink {
  0%, 50%       { opacity: 1; }
  50.01%, 100%  { opacity: 0; }
}

.progress-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  animation: lineAppear 0.3s ease-out;
}

.progress-bar-terminal {
  flex: 1;
  height: 24px;
  background: #2d2d2d;
  border: 1px solid #3d3d3d;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill-terminal {
  height: 100%;
  background: linear-gradient(90deg, #5ce1e6 0%, #4facfe 100%);
  display: flex;
  align-items: center;
  padding-left: 8px;
  transition: width 0.05s linear;
}

.progress-blocks {
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  color: #000000;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
}

.progress-percent {
  font-family: 'Fira Code', monospace;
  font-size: 14px;
  color: #5ce1e6;
  font-weight: 600;
  min-width: 48px;
  text-align: right;
}

/* ─── Transitions ─────────────────────────────── */
.loader-fade-leave-active {
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}
.loader-fade-leave-to {
  opacity: 0;
  transform: scale(1.05);
  filter: blur(10px);
}

.page-fade-enter-active {
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
.page-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

/* ─── Global Scrollbar ────────────────────────── */
::-webkit-scrollbar        { width: 10px; height: 10px; }
::-webkit-scrollbar-track  { background: #0a0a0a; }
::-webkit-scrollbar-thumb  {
  background: linear-gradient(180deg, #5ce1e6 0%, #4facfe 100%);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #4facfe 0%, #5ce1e6 100%);
  box-shadow: 0 0 10px rgba(92, 225, 230, 0.5);
}

* {
  scrollbar-width: thin;
  scrollbar-color: #5ce1e6 #0a0a0a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html { scroll-behavior: smooth; }
body { overflow-x: hidden; }

::selection {
  background: rgba(92, 225, 230, 0.3);
  color: #ffffff;
}

/* ─── Responsive ──────────────────────────────── */
@media (max-width: 768px) {
  .terminal-window  { max-width: 95vw; }
  .terminal-body    { padding: 16px; min-height: 280px; font-size: 12px; }
  .terminal-title   { font-size: 11px; }
  .terminal-buttons span { width: 10px; height: 10px; }
  .progress-bar-terminal { height: 20px; }
  .progress-blocks  { font-size: 10px; }
  .progress-percent { font-size: 12px; }
}

@media (max-width: 480px) {
  .terminal-body   { padding: 12px; font-size: 11px; line-height: 1.6; min-height: 250px; }
  .terminal-header { padding: 10px 12px; }
  .progress-wrapper { flex-direction: column; align-items: stretch; gap: 8px; }
  .progress-percent { text-align: center; }
}
</style>